import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { LayoutDashboard, Wrench, Mail, Settings, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, User, LogOut as LogOutIcon, Search, LayoutGrid } from 'lucide-react';
import { initKmfcClient, koffieClient } from '../lib/supabase';
import { clearKoffieSession, useKoffieNav } from './KoffieApp';
import { saveWhenconnectSession } from '../whenconnect/WhenconnectApp';

const formatUsername = (name) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '';

// ─── Tokens ───────────────────────────────────────────────────────────────────

const t = {
  bg:     '#f2f0eb',
  white:  '#ffffff',
  text:   '#0c0c0b',
  muted:  '#8a8780',
  body:   '#5b5953',
  lines:  '#dcd9d1',
  accent: '#c75a0a',
  sans:   "'Jost', system-ui, sans-serif",
};

// ─── Context ──────────────────────────────────────────────────────────────────

const KoffieLayoutContext = createContext({ onLogout: () => {} });
export const useKoffieLayout = () => useContext(KoffieLayoutContext);

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Shell ────────────────────────────────────────────────────────────────────

const AppShell = styled.div`
  display: flex;
  height: 100vh;
  background: ${t.bg};
  overflow: hidden;
  font-family: ${t.sans};
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const SideNav = styled.nav`
  width: ${p => p.$collapsed ? '56px' : '212px'};
  min-width: ${p => p.$collapsed ? '56px' : '212px'};
  background: ${t.white};
  display: flex;
  flex-direction: column;
  padding: 0 0 24px;
  position: relative;
  transition: width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1);
  overflow: hidden;
  flex-shrink: 0;
`;

const SideNavLogo = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 ${p => p.$collapsed ? '0' : '16px'};
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const Logo = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;
`;

const NavSection = styled.div`
  padding: 0 8px;
`;

const NavSectionLabel = styled.div`
  display: none;
  margin: 0;
  padding: 0;
`;

const NavDivider = styled.div`
  height: 1px;
  background: ${t.lines};
  margin: 8px 10px;
`;

const SideNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  color: ${p => p.$active ? t.text : t.muted};
  text-decoration: none;
  border-radius: 6px;
  font-family: ${t.sans};
  font-weight: ${p => p.$active ? '600' : '400'};
  font-size: 13px;
  background: transparent;
  transition: color 0.15s;

  &:hover {
    color: ${t.text};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: ${p => p.$active ? 1 : 0.4};
    color: ${p => p.$active ? t.accent : 'inherit'};
  }
`;

const NavItemLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  display: ${p => p.$collapsed ? 'none' : 'block'};
`;

const ExternalBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  color: ${t.muted};
  background: transparent;
  border: none;
  border-radius: 6px;
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  &:hover { color: ${t.text}; background: rgba(0,0,0,0.04); }
  svg { width: 16px; height: 16px; opacity: 0.5; flex-shrink: 0; }
`;

const SideNavAppInfo = styled.div`
  margin: auto 0 0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid ${t.lines};
`;

const SideNavAppName = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 500;
  color: ${t.muted};
  white-space: nowrap;
`;

const SideNavAppMeta = styled.div`
  font-family: ${t.sans};
  font-size: 10px;
  color: ${t.muted};
  opacity: 0.5;
  margin-top: 2px;
  white-space: nowrap;
`;

const CollapseBtn = styled.button`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid ${t.lines};
  background: ${t.white};
  color: ${t.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: ${t.bg}; color: ${t.text}; }
`;

// ─── TopBar ───────────────────────────────────────────────────────────────────

const TopBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 24px 0 0;
  height: 64px;
  background: ${t.white};
  flex-shrink: 0;
`;

const TopBarLeft = styled.div`
  width: 0px;
  flex-shrink: 0;
`;

const TopBarSearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${t.bg};
  border: 1px solid ${p => p.$focused ? t.text : t.lines};
  border-radius: 6px;
  padding: 0 12px;
  height: 32px;
  width: 220px;
  transition: border-color 0.15s;
  svg { color: ${t.muted}; opacity: 0.5; flex-shrink: 0; }
`;

const TopBarSearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.text};
  &::placeholder { color: ${t.muted}; opacity: 0.5; }
`;

const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 280px;
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.10);
  z-index: 10000;
  overflow: hidden;
`;

const SearchResultGroup = styled.div`
  padding: 6px;
  &:not(:last-child) { border-bottom: 1px solid ${t.lines}; }
`;

const SearchResultLabel = styled.div`
  font-family: ${t.sans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${t.muted};
  padding: 4px 8px 6px;
`;

const SearchResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  background: none;
  border-radius: 5px;
  cursor: pointer;
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.text};
  text-align: left;
  transition: background 0.1s;
  &:hover { background: ${t.bg}; }
`;

const SearchEmpty = styled.div`
  padding: 14px 16px;
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.muted};
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  justify-content: flex-end;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px 4px 4px;
  border-radius: 6px;
  transition: background 0.15s;
  &:hover { background: rgba(0,0,0,0.04); }
`;

const ProfileAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: ${t.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
`;

const ProfileName = styled.span`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 500;
  color: ${t.text};
`;

const ProfileDropdown = styled.div`
  position: fixed;
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  padding: 6px;
  min-width: 190px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.10);
  z-index: 10000;
  animation: ${fadeIn} 0.15s cubic-bezier(0.16,1,0.3,1);
`;

const ProfileDropdownHeader = styled.div`
  font-family: ${t.sans};
  padding: 6px 10px 8px;
  font-size: 12px;
  color: ${t.muted};
`;

const ProfileDropdownDivider = styled.div`
  height: 1px;
  background: ${t.lines};
  margin: 4px 0;
`;

const ProfileDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$danger ? '#c0392b' : t.text};
  text-align: left;
  transition: background 0.12s;
  &:hover { background: ${p => p.$danger ? 'rgba(192,57,43,0.07)' : 'rgba(0,0,0,0.04)'}; }
  svg { opacity: 0.5; flex-shrink: 0; }
`;

// ─── Logout / confirm modal ───────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12,12,11,0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalBox = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  max-width: 360px;
  width: 90%;
  padding: 28px;
`;

const ModalTitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 22px;
  font-weight: 900;
  color: ${t.text};
  margin: 0 0 8px;
`;

const ModalText = styled.p`
  font-family: ${t.sans};
  font-size: 14px;
  color: ${t.body};
  margin: 0 0 24px;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ModalBtn = styled.button`
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 11px 20px;
  border-radius: 6px;
  border: 1.5px solid ${p => p.$danger ? '#c0392b' : t.lines};
  background: ${p => p.$danger ? '#c0392b' : 'transparent'};
  color: ${p => p.$danger ? '#fff' : t.text};
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.8; }
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ConfirmModal({ title, text, onCancel, onConfirm, confirmLabel = 'Doorgaan' }) {
  return ReactDOM.createPortal(
    <ModalOverlay onClick={onCancel}>
      <ModalBox onClick={e => e.stopPropagation()}>
        <ModalTitle>{title}</ModalTitle>
        <ModalText>{text}</ModalText>
        <ModalButtons>
          <ModalBtn onClick={onCancel}>Annuleren</ModalBtn>
          <ModalBtn $danger onClick={onConfirm}>{confirmLabel}</ModalBtn>
        </ModalButtons>
      </ModalBox>
    </ModalOverlay>,
    document.body
  );
}

const isSuperAdmin = () => { try { return !!JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return false; } };
const getSuperAdmin = () => { try { return JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return null; } };

const SWITCH_COMPANIES = [
  { slug: 'koffiereparatie-geleen', name: 'Koffiereparatie Geleen', logo: '/assets/Koffie-geleen_logo.png' },
  { slug: 'kmfc-shop',             name: 'KMFC Shop',              logo: 'https://kmfcshoponline.nl/assets/logo.png' },
  { slug: 'whenconnect',           name: 'Whenconnect',            logo: null },
];

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function KoffieLayout({ children, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('koffie_sidebar_collapsed') === 'true');
  const [showProfile, setShowProfile] = useState(false);
  const [profilePos, setProfilePos] = useState({ top: 0, right: 0 });
  const [showLogout, setShowLogout] = useState(false);
  const [showWebsite, setShowWebsite] = useState(false);
  const profileRef = useRef(null);
  const { handleNavigation } = useKoffieNav();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('koffie_session') || '{}'); } catch { return {}; } })();
  const username = formatUsername(storedUser.username || storedUser.name) || 'Gebruiker';
  const initials = username.slice(0, 2).toUpperCase();

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('koffie_sidebar_collapsed', next ? 'true' : 'false');
  };

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
        setSearchResults(null);
        setSearchQuery('');
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const SETTINGS_ITEMS = [
    { label: 'Instellingen', path: '/Koffiereparatie/settings' },
    { label: 'Wachtwoord wijzigen', path: '/Koffiereparatie/settings/password' },
    { label: 'Pincode', path: '/Koffiereparatie/settings/pin' },
    { label: 'Wettelijke documentatie', path: '/Koffiereparatie/settings/legal' },
    { label: 'Algemene voorwaarden', path: '/Koffiereparatie/settings/terms' },
    { label: 'Overzicht', path: '/Koffiereparatie/dashboard' },
    { label: 'Machines', path: '/Koffiereparatie/machines' },
    { label: 'Contactinzendingen', path: '/Koffiereparatie/contact' },
  ];

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimerRef.current);
    if (!q.trim()) { setSearchResults(null); return; }

    const lq = q.toLowerCase();

    const isWebsite = ['website', 'koffiereparatie', 'koffiereparatie-geleen', 'koffiereparatie-geleen.nl'].some(w => w.startsWith(lq) || lq.includes(w.split('.')[0]));

    const matchedSettings = SETTINGS_ITEMS.filter(s => s.label.toLowerCase().includes(lq));

    searchTimerRef.current = setTimeout(async () => {
      const [{ data: machines }, { data: contacts }] = await Promise.all([
        koffieClient.from('koffiemachines')
          .select('id, naam, type, prijs, kenmerk_1, kenmerk_2, kenmerk_3')
          .or(`naam.ilike.%${q}%,type.ilike.%${q}%,kenmerk_1.ilike.%${q}%,kenmerk_2.ilike.%${q}%,kenmerk_3.ilike.%${q}%`)
          .limit(5),
        koffieClient.from('contact-submissions')
          .select('id, name, email, message, phone')
          .or(`name.ilike.%${q}%,email.ilike.%${q}%,message.ilike.%${q}%,phone.ilike.%${q}%`)
          .limit(5),
      ]);
      setSearchResults({
        website: isWebsite,
        settings: matchedSettings,
        machines: machines || [],
        contacts: contacts || [],
      });
    }, 250);
  };

  const handleSearchSelect = (path) => {
    setSearchFocused(false);
    setSearchResults(null);
    setSearchQuery('');
    handleNavigation(() => navigate(path));
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const switchCompany = async (slug) => {
    if (slug === 'koffiereparatie-geleen') return;
    const sa = getSuperAdmin();
    if (!sa) return;
    const base = {
      id: sa.id, username: sa.username,
      company_slug: slug, company_name: SWITCH_COMPANIES.find(c => c.slug === slug)?.name || slug,
      role_id: 1,
      role: { slug: 'admin', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
      access_token: sa.access_token, refresh_token: sa.refresh_token,
      expires_at: sa.expires_at, login_time: new Date().toISOString(),
    };
    if (slug === 'whenconnect') {
      saveWhenconnectSession(base);
      window.location.hash = '/Whenconnect/dashboard';
      window.location.reload();
    } else {
      await initKmfcClient();
      localStorage.setItem('kmfc_user', JSON.stringify(base));
      window.location.hash = '/KMFC/dashboard';
      window.location.reload();
    }
  };

  const NAV_ITEMS = [
    { label: 'Overzicht', icon: LayoutDashboard, path: '/Koffiereparatie/dashboard' },
    { label: 'Machines', icon: Wrench, path: '/Koffiereparatie/machines' },
    { label: 'Contactinzendingen', icon: Mail, path: '/Koffiereparatie/contact' },
    { label: 'Instellingen', icon: Settings, path: '/Koffiereparatie/settings' },
  ];

  return (
    <KoffieLayoutContext.Provider value={{ onLogout }}>
      <AppShell>
        {/* Sidebar */}
        <SideNav $collapsed={collapsed}>
          <SideNavLogo $collapsed={collapsed}>
            {!collapsed && <Logo src={`${process.env.PUBLIC_URL}/assets/Webvonk.png`} alt="Webvonk" />}
          </SideNavLogo>

          <NavSection>
            <NavSectionLabel $collapsed={collapsed}>Navigatie</NavSectionLabel>
            {NAV_ITEMS.map(({ label, icon: Icon, path }) => (
              <SideNavItem key={path} to={path} $active={isActive(path)} $collapsed={collapsed}>
                <Icon size={16} />
                <NavItemLabel $collapsed={collapsed}>{label}</NavItemLabel>
              </SideNavItem>
            ))}
          </NavSection>


          <NavDivider />

          <NavSection>
            <NavSectionLabel $collapsed={collapsed}>Extern</NavSectionLabel>
            <ExternalBtn $collapsed={collapsed} onClick={() => setShowWebsite(true)}>
              <ExternalLink size={16} />
              <NavItemLabel $collapsed={collapsed}>Website</NavItemLabel>
            </ExternalBtn>
          </NavSection>

          <SideNavAppInfo>
            {!collapsed && (
              <div>
                <SideNavAppName>Koffiereparatie Geleen</SideNavAppName>
                <SideNavAppMeta>© {new Date().getFullYear()} Webvonk</SideNavAppMeta>
              </div>
            )}
            <CollapseBtn onClick={toggleCollapsed} style={collapsed ? { margin: '0 auto' } : {}}>
              {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
            </CollapseBtn>
          </SideNavAppInfo>
        </SideNav>

        {/* Main */}
        <MainArea>
          <TopBarContainer>
            <TopBarLeft />
            <TopBarSearchWrap $focused={searchFocused} ref={searchRef} onClick={e => e.stopPropagation()}>
              <Search size={13} />
              <TopBarSearchInput
                placeholder="Zoeken…"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
              />
              {searchFocused && searchResults && (
                <SearchResults>
                  {!searchResults.website && searchResults.settings.length === 0 && searchResults.machines.length === 0 && searchResults.contacts.length === 0 ? (
                    <SearchEmpty>Geen resultaten gevonden</SearchEmpty>
                  ) : (
                    <>
                      {searchResults.website && (
                        <SearchResultGroup>
                          <SearchResultLabel>Website</SearchResultLabel>
                          <SearchResultItem onClick={() => { setSearchFocused(false); setSearchResults(null); setSearchQuery(''); window.open('https://koffiereparatie-geleen.nl/', '_blank', 'noopener,noreferrer'); }}>
                            <ExternalLink size={13} style={{ color: t.muted, opacity: 0.5, flexShrink: 0 }} />
                            koffiereparatie-geleen.nl
                          </SearchResultItem>
                        </SearchResultGroup>
                      )}
                      {searchResults.settings.length > 0 && (
                        <SearchResultGroup>
                          <SearchResultLabel>Pagina's</SearchResultLabel>
                          {searchResults.settings.map(s => (
                            <SearchResultItem key={s.path} onClick={() => handleSearchSelect(s.path)}>
                              <Settings size={13} style={{ color: t.muted, opacity: 0.5, flexShrink: 0 }} />
                              {s.label}
                            </SearchResultItem>
                          ))}
                        </SearchResultGroup>
                      )}
                      {searchResults.machines.length > 0 && (
                        <SearchResultGroup>
                          <SearchResultLabel>Machines</SearchResultLabel>
                          {searchResults.machines.map(m => (
                            <SearchResultItem key={m.id} onClick={() => handleSearchSelect('/Koffiereparatie/machines')}>
                              <Wrench size={13} style={{ color: t.muted, opacity: 0.5, flexShrink: 0 }} />
                              <span>{m.naam}{m.type ? <span style={{ color: t.muted, fontSize: 11, marginLeft: 6 }}>{m.type}</span> : null}</span>
                            </SearchResultItem>
                          ))}
                        </SearchResultGroup>
                      )}
                      {searchResults.contacts.length > 0 && (
                        <SearchResultGroup>
                          <SearchResultLabel>Contactinzendingen</SearchResultLabel>
                          {searchResults.contacts.map(c => (
                            <SearchResultItem key={c.id} onClick={() => handleSearchSelect('/Koffiereparatie/contact')}>
                              <Mail size={13} style={{ color: t.muted, opacity: 0.5, flexShrink: 0 }} />
                              <span>{c.name || c.email}{c.name && c.email ? <span style={{ color: t.muted, fontSize: 11, marginLeft: 6 }}>{c.email}</span> : null}</span>
                            </SearchResultItem>
                          ))}
                        </SearchResultGroup>
                      )}
                    </>
                  )}
                </SearchResults>
              )}
            </TopBarSearchWrap>
            <TopBarRight>
              <div ref={profileRef}>
                <ProfileButton onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setProfilePos({ top: r.bottom + 8, right: window.innerWidth - r.right });
                  setShowProfile(p => !p);
                }}>
                  <ProfileAvatar>{initials}</ProfileAvatar>
                  <ProfileName>{username}</ProfileName>
                  <ChevronDown size={13} style={{ opacity: 0.4, color: t.text, transition: 'transform 0.2s', transform: showProfile ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </ProfileButton>
              </div>
            </TopBarRight>
          </TopBarContainer>

          <ContentArea>{children}</ContentArea>
        </MainArea>
      </AppShell>

      {showProfile && ReactDOM.createPortal(
        <ProfileDropdown style={{ top: profilePos.top, right: profilePos.right }} onClick={e => e.stopPropagation()}>
          <ProfileDropdownHeader>{username}</ProfileDropdownHeader>
          <ProfileDropdownDivider />
          <ProfileDropdownItem onClick={() => { setShowProfile(false); handleNavigation(() => navigate('/Koffiereparatie/settings')); }}>
            <User size={14} /> Account
          </ProfileDropdownItem>
          <ProfileDropdownItem onClick={() => { setShowProfile(false); handleNavigation(() => navigate('/Koffiereparatie/settings')); }}>
            <Settings size={14} /> Instellingen
          </ProfileDropdownItem>
          {(() => { try { return JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return null; } })() && (
            <ProfileDropdownItem onClick={() => { setShowProfile(false); window.location.href = '/#/company-select'; }}>
              <LayoutGrid size={14} /> Wissel bedrijf
            </ProfileDropdownItem>
          )}
          <ProfileDropdownDivider />
          <ProfileDropdownItem $danger onClick={() => { setShowProfile(false); setShowLogout(true); }}>
            <LogOutIcon size={14} /> Uitloggen
          </ProfileDropdownItem>
        </ProfileDropdown>,
        document.body
      )}

      {showLogout && (
        <ConfirmModal
          title="Uitloggen"
          text="Weet je zeker dat je wilt uitloggen?"
          confirmLabel="Uitloggen"
          onCancel={() => setShowLogout(false)}
          onConfirm={() => { setShowLogout(false); clearKoffieSession(); onLogout(); }}
        />
      )}

      {showWebsite && (
        <ConfirmModal
          title="Bekijk website"
          text="Je wordt doorgestuurd naar koffiereparatie-geleen.nl."
          confirmLabel="Open website"
          onCancel={() => setShowWebsite(false)}
          onConfirm={() => { setShowWebsite(false); window.open('https://koffiereparatie-geleen.nl/', '_blank', 'noopener,noreferrer'); }}
        />
      )}
    </KoffieLayoutContext.Provider>
  );
}
